import React from 'react';
import { Table, Select, Tag, Modal, Tooltip, Input, Button, Tabs, Switch, Alert, Typography } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import request from '../../utils/request';
import { useAuth } from '../../hooks/useAuth';
import { getProjectList } from '../../services/project';

type BillingRulesProps = { extraFiltersStart?: React.ReactNode };

const BillingRules: React.FC<BillingRulesProps> = ({ extraFiltersStart }) => {
  const [custData, setCustData] = React.useState<any[]>([]);
  const [projData, setProjData] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [locale, setLocale] = React.useState<string | undefined>('zh-CN');
  const { user } = useAuth();
  const [tierViewOpen, setTierViewOpen] = React.useState(false);
  const [tierViewRule, setTierViewRule] = React.useState<any | null>(null);
  const [tierViewList, setTierViewList] = React.useState<any[]>([]);
  const [tierViewLoading, setTierViewLoading] = React.useState(false);

  const [levelFilter, setLevelFilter] = React.useState<string | undefined>(undefined);
  const [typeFilter, setTypeFilter] = React.useState<string | undefined>(undefined);
  const [categoryFilter, setCategoryFilter] = React.useState<string | undefined>(undefined);
  const [keyword, setKeyword] = React.useState<string>('');
  // 按收费规则系统编码模糊筛选：纯前端、输入即筛选（不重新请求接口）
  const [codeKeyword, setCodeKeyword] = React.useState<string>('');
  const [custPage, setCustPage] = React.useState<number>(1);
  const [custPageSize, setCustPageSize] = React.useState<number>(50);
  const [projPage, setProjPage] = React.useState<number>(1);
  const [projPageSize, setProjPageSize] = React.useState<number>(50);
  const [activeTab, setActiveTab] = React.useState<'customer'|'project'>('customer');

  // 项目选择与差异开关
  const [customerId, setCustomerId] = React.useState<number | undefined>(undefined);
  const [projectId, setProjectId] = React.useState<number | undefined>(undefined);
  const [projectOptions, setProjectOptions] = React.useState<Array<{ value: number; label: string }>>([]);
  const [projectLoading, setProjectLoading] = React.useState<boolean>(false);
  // diffOnly 由“仅差异”标签页承载
  const [projectSelectModalOpen, setProjectSelectModalOpen] = React.useState<boolean>(false);
  const [projectHeaderLabel, setProjectHeaderLabel] = React.useState<string>('');
  const [onlyProjExclusive, setOnlyProjExclusive] = React.useState<boolean>(false);
  // 显示已屏蔽开关（默认不显示）
  const [showMaskedCust, setShowMaskedCust] = React.useState<boolean>(false);
  const [showMaskedProj, setShowMaskedProj] = React.useState<boolean>(false);

  const fetchEffective = async () => {
    setLoading(true);
    try {
      // 获取 customer_id / project_id
      let cid: number | undefined = customerId;
      let pid: number | undefined = projectId;
      try {
        const url = new URL(window.location.href);
        const qcid = url.searchParams.get('customer_id');
        const qpid = url.searchParams.get('project_id');
        if (!cid && qcid) cid = Number(qcid);
        if (!pid && qpid) pid = Number(qpid);
      } catch {}
      if (!cid && user && user.role === 'customer') cid = user.id;
      setCustomerId(cid);

      // 拉取客户级
      if (cid) {
        const resCust = await request.get('/api/v1/billing/billing-rules/effective/', { params: { customer_id: cid, locale, customer_portal: 1 } });
        setCustData(Array.isArray(resCust.data) ? resCust.data : []);
      } else {
        setCustData([]);
      }
      // 拉取项目级（选中项目才拉）
      if (cid && pid) {
        const resProj = await request.get('/api/v1/billing/billing-rules/effective/', { params: { customer_id: cid, project_id: pid, locale, customer_portal: 1 } });
        setProjData(Array.isArray(resProj.data) ? resProj.data : []);
      } else {
        setProjData([]);
      }
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    setCustPage(1);
    setProjPage(1);
    fetchEffective();
  }, [locale, levelFilter, typeFilter, categoryFilter, keyword, customerId, projectId]);

  const searchProjects = async (search?: string) => {
    const cid = customerId || (user && user.role === 'customer' ? user.id : undefined);
    if (!cid) { setProjectOptions([]); return; }
    setProjectLoading(true);
    try {
      const resp = await getProjectList({ page: 1, page_size: 50, search: search || '', customer: cid });
      const results = Array.isArray((resp.data as any)?.results) ? (resp.data as any).results : [];
      const opts = results.map((p: any) => ({ value: Number(p.id), label: `${p.project_code || p.id} - ${p.name}` }));
      setProjectOptions(opts);
    } catch {
      setProjectOptions([]);
    } finally {
      setProjectLoading(false);
    }
  };

  // 过滤函数
  const applyFilters = React.useCallback((list: any[]) => {
    const kw = (keyword || '').trim().toLowerCase();
    const codeKw = (codeKeyword || '').trim().toLowerCase();
    return (list || []).filter((r) => {
      if (typeFilter && r.rule_type !== typeFilter) return false;
      if (categoryFilter && r.operation_type_category !== categoryFilter) return false;
      if (codeKw && !String(r.system_code ?? '').toLowerCase().includes(codeKw)) return false;
      if (kw) {
        const name = (r.display_name || r.operation_type_name || '').toString().toLowerCase();
        if (!name.includes(kw)) return false;
      }
      return true;
    });
  }, [typeFilter, categoryFilter, keyword, codeKeyword]);

  // 编码筛选条件变了回到第一页（它不触发重新请求，所以不在上面那个请求用的 effect 里）
  React.useEffect(() => {
    setCustPage(1);
    setProjPage(1);
  }, [codeKeyword]);

  const custFiltered = React.useMemo(() => applyFilters(custData), [custData, applyFilters]);
  const custFilteredFinal = React.useMemo(() => {
    if (showMaskedCust) return custFiltered;
    return custFiltered.filter((r: any) => !r.is_masked);
  }, [custFiltered, showMaskedCust]);
  const projFilteredRaw = React.useMemo(() => applyFilters(projData), [projData, applyFilters]);
  const projFilteredForTable = React.useMemo(() => {
    const base = showMaskedProj ? projFilteredRaw : projFilteredRaw.filter((r) => !r.is_masked);
    if (!onlyProjExclusive) return base;
    return base.filter((r) => (r.source_level ?? r.rule_level) === 'project');
  }, [projFilteredRaw, onlyProjExclusive, showMaskedProj]);


  const openTierView = async (rule: any) => {
    setTierViewRule(rule);
    setTierViewOpen(true);
    setTierViewLoading(true);
    try {
      // 优先使用 effective 响应内置的 tiers_preview，避免 404 取不到 id 的情况
      if (Array.isArray(rule.tiers_preview)) {
        setTierViewList(rule.tiers_preview);
      } else if (rule.id) {
        const res = await request.get(`/api/v1/billing/billing-rules/${rule.id}/tiers/`, { params: { locale } });
        setTierViewList(res.data || []);
      } else {
        setTierViewList([]);
      }
    } finally {
      setTierViewLoading(false);
    }
  };

  // CSV 导出相关
  const csvEscape = (val: any) => {
    const s = val == null ? '' : String(val);
    const escaped = s.replace(/"/g, '""');
    return `"${escaped}"`;
  };
  const buildTiersSummary = (r: any) => {
    if (!Array.isArray(r?.tiers_preview)) return '';
    try {
      return r.tiers_preview
        .map((t: any) => `${t?.min_value ?? 0}-${t?.max_value ?? ''}:${t?.unit_price ?? ''}`)
        .join(' | ');
    } catch {
      return '';
    }
  };
  const buildCsv = (rows: any[]) => {
    const headers = [
      '收费规则系统编码', '操作类型', '收费项目名称', '收费单位', '状态', '收费类型', '单价/区间', '币种',
      '维度', '区间最小', '区间最大', '变体键', '生效起', '生效止', '说明', '举例', '梯度详情'
    ];
    const lines = [] as string[];
    lines.push(headers.map(csvEscape).join(','));
    for (const r of rows || []) {
      const ruleTypeLabel = r?.rule_type === 'tiered' ? '梯度收费' : '固定收费';
      const statusLabel = r?.is_masked ? '屏蔽' : (r?.is_active ? '启用' : '禁用');
      const priceDisplay = r?.is_masked
        ? '-'
        : (r?.rule_type === 'tiered'
            ? (r?.unit_price_display ?? '')
            : (r?.unit_price != null ? Number(r.unit_price).toFixed(2) : '')
          );
      const record = [
        r?.system_code ?? '',
        r?.operation_type_name ?? '',
        r?.display_name ?? r?.operation_type_name ?? '',
        r?.unit_label ?? '',
        statusLabel,
        ruleTypeLabel,
        priceDisplay,
        r?.currency ?? '',
        r?.tier_basis ?? '',
        r?.min_value ?? '',
        r?.max_value ?? '',
        r?.variant_key ?? '',
        r?.effective_from ?? '',
        r?.effective_to ?? '',
        r?.description ?? '',
        r?.examples ?? '',
        buildTiersSummary(r),
      ];
      lines.push(record.map(csvEscape).join(','));
    }
    return lines.join('\r\n');
  };
  const downloadCsv = (rows: any[], fileName: string) => {
    const csv = buildCsv(rows);
    const blob = new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileName}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  // 收费规则系统编码列：带复制图标（客户核对、反馈问题时直接复制编码）
  const renderRuleCode = (v: any) => (
    v ? <Typography.Text copyable={{ text: String(v), tooltips: ['复制', '已复制'] }}>{v}</Typography.Text> : '-'
  );
  const handleDownloadCustomerCSV = () => {
    downloadCsv(custData, `billing_rules_customer_${customerId ?? 'unknown'}`);
  };
  const handleDownloadProjectCSV = () => {
    downloadCsv(projData, `billing_rules_project_${projectId ?? 'unknown'}`);
  };

  return (
    <div className="p-6">
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">收费规则</h1>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">显示语言:</span>
            <Select
              style={{ width: 140 }}
              value="zh-CN"
              onChange={(v) => {
                if (v === 'zh-CN') setLocale('zh-CN');
              }}
              options={[
                { value: 'zh-CN', label: '中文' },
                { value: 'en-US', label: 'English', disabled: true }
              ]}
            />
          </div>
        </div>
        <div className="mt-2 flex items-center gap-3 flex-wrap">
          {extraFiltersStart}
          <Select
            placeholder="收费类型"
            allowClear
            style={{ width: 140 }}
            value={typeFilter}
            onChange={(v) => setTypeFilter(v)}
            options={[
              { value: 'flat', label: '固定收费' },
              { value: 'tiered', label: '梯度收费' },
            ]}
          />
          <Select
            placeholder="操作类别"
            allowClear
            style={{ width: 160 }}
            value={categoryFilter}
            onChange={(v) => setCategoryFilter(v)}
            options={[
              { value: 'INBOUND', label: '入库' },
              { value: 'OUTBOUND', label: '出库' },
              { value: 'INVENTORY_MANAGEMENT', label: '在库管理' },
              { value: 'VALUE_ADDED', label: '增值服务' },
              { value: 'ADJUSTMENT', label: '调整' },
            ]}
          />
          {/* 顶部移除项目选择，下方在项目级页头提供“选择其他项目”按钮 */}
          <Input.Search
            allowClear
            placeholder="搜索收费项目名称"
            style={{ width: 240 }}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
          <Input
            allowClear
            placeholder="按收费规则系统编码筛选"
            style={{ width: 240 }}
            value={codeKeyword}
            onChange={(e) => setCodeKeyword(e.target.value)}
          />
          <Button onClick={() => { setTypeFilter(undefined); setCategoryFilter(undefined); setKeyword(''); setCodeKeyword(''); setProjectId(undefined); setCustPage(1); setProjPage(1); setCustPageSize(50); setProjPageSize(50); }}>重置</Button>
        </div>
      </div>
      <Tabs activeKey={activeTab} onChange={(k) => {
        setActiveTab(k as any);
        if (k === 'project' && !projectId) {
          setProjectSelectModalOpen(true);
        }
      }} items={[
        { key: 'customer', label: '客户级', children: (
          <div>
            <div className="mb-2 flex items-center justify-between">
              <div className="font-semibold">客户级 - 最终生效规则</div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-600">显示已屏蔽计费规则</span>
                <Switch size="small" checked={showMaskedCust} onChange={(v) => setShowMaskedCust(v)} />
                <Button size="small" type="primary" onClick={handleDownloadCustomerCSV}>下载客户级完整收费规则</Button>
              </div>
            </div>
          <Table
            rowKey={(r) => `${r.operation_type}-${r.billing_unit}-${r.min_value}-${r.max_value}-${r.source_level}-${r.variant_key || 'NA'}`}
            loading={loading}
            dataSource={custFilteredFinal}
            pagination={{ current: custPage, pageSize: custPageSize, total: custFilteredFinal.length, showSizeChanger: true, pageSizeOptions: [10,20,50,100] as any, showTotal: (total, range) => `共${total}条，当前${range[0]}-${range[1]}条` }}
            onChange={(p) => { setCustPage(p.current || 1); setCustPageSize(p.pageSize || 50); }}
            columns={[
          { title: '收费规则系统编码', dataIndex: 'system_code', width: 200, render: renderRuleCode },
          { title: '操作类型', dataIndex: 'operation_type_name', width: 160 },
          { title: '收费项目名称', dataIndex: 'display_name', width: 220, render: (v: any, r: any) => v || r.operation_type_name },
          { title: '收费单位', dataIndex: 'unit_label', width: 120, render: (v: string) => v || '-' },
          { title: (
            <span>
              状态
              <Tooltip title="“启用”表示此计费规则生效并正常计费；“屏蔽”表示此计费规则未生效（不计费）">
                <QuestionCircleOutlined style={{ marginLeft: 6, color: '#999' }} />
              </Tooltip>
            </span>
          ), dataIndex: 'is_active', width: 100, render: (v: boolean, r: any) => {
            if (r?.is_masked && v) return <Tag color="red">屏蔽</Tag>;
            if (v) return <Tag color="green">启用</Tag>;
            return <Tag>禁用</Tag>;
          } },
          { title: '收费类型', dataIndex: 'rule_type', width: 100, render: (v: string) => {
            const map: any = {
              flat: { label: '固定收费', color: 'blue' },
              tiered: { label: '梯度收费', color: 'magenta' },
            };
            const key = v === 'tiered' ? 'tiered' : 'flat';
            const cfg = map[key];
            return <Tag color={cfg.color}>{cfg.label}</Tag>;
          } },
          { title: '梯度维度', dataIndex: 'tier_basis', width: 120, render: (v: string, r: any) => {
            if (r?.rule_type !== 'tiered') return '-';
            const label = ({
              QUANTITY: '数量',
              WEIGHT_G: '重量(g)',
              WEIGHT_KG: '重量(kg)',
              VOLUME_CUFT: '体积(ft³)',
              VOLUME_CBM: '体积(m³)',
              VOLUME_CC: '体积(cm³)',
              AGE_DAY: '库龄(天)'
            } as any)[v || ''];
            return label || '-';
          } },
          { title: (
            <span>
              单价
              <Tooltip title="请点击单价查看梯度收费详情">
                <QuestionCircleOutlined style={{ marginLeft: 6, color: '#999' }} />
              </Tooltip>
            </span>
          ), width: 140, render: (_: any, r: any) => {
            if (r.is_masked) {
              return <span title="屏蔽不计费">-</span>;
            }
            if (r.rule_type === 'tiered') {
              const text = r.unit_price_display || '查看梯度';
              return (
                <span
                  style={{ color: '#1677ff', cursor: 'pointer' }}
                  onClick={() => openTierView(r)}
                  title="查看梯度设置详情"
                >
                  {text}
                </span>
              );
            }
            const v = r.unit_price;
            return v != null ? Number(v).toFixed(2) : '-';
          } },
          { title: '币种', dataIndex: 'currency', width: 80 },
          { title: '说明', dataIndex: 'description', width: 360, render: (v: any) => (
            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{v || '-'}</div>
          ) },
          { title: '举例', dataIndex: 'examples', width: 360, render: (v: any) => (
            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{v || '-'}</div>
          ) },
            ]}
          />
          </div>
        ) },
        { key: 'project', label: '项目级', children: (
          <div>
            <div className="mb-2 flex items-center justify-between">
              <div className="font-semibold flex items-center flex-wrap gap-8">
                <span>项目级 - 最终生效规则</span>
                {projectId ? (
                  <>
                    <span className="text-sm text-gray-600">当前项目：{projectHeaderLabel || projectId}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-600">仅显示项目专属规则</span>
                      <Switch size="small" checked={onlyProjExclusive} onChange={(v) => setOnlyProjExclusive(v)} />
                    </div>
                    <Button size="small" type="primary" className="ml-4" onClick={() => setProjectSelectModalOpen(true)}>选择其他项目</Button>
                  </>
                ) : (
                  <Button size="small" type="primary" onClick={() => setProjectSelectModalOpen(true)}>选择项目</Button>
                )}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-600">显示已屏蔽计费规则</span>
                <Switch size="small" checked={showMaskedProj} onChange={(v) => setShowMaskedProj(v)} />
                <Button size="small" type="primary" onClick={handleDownloadProjectCSV} disabled={!projectId}>下载项目级完整收费规则</Button>
              </div>
            </div>
          <Table
            rowKey={(r) => `${r.operation_type}-${r.billing_unit}-${r.min_value}-${r.max_value}-${r.source_level}-${r.variant_key || 'NA'}`}
            loading={loading}
            dataSource={projFilteredForTable}
            pagination={{ current: projPage, pageSize: projPageSize, total: projFilteredForTable.length, showSizeChanger: true, pageSizeOptions: [10,20,50,100] as any, showTotal: (total, range) => `共${total}条，当前${range[0]}-${range[1]}条` }}
            onChange={(p) => { setProjPage(p.current || 1); setProjPageSize(p.pageSize || 50); }}
            onRow={(record) => {
              const isProjExclusive = (record?.source_level ?? record?.rule_level) === 'project';
              return isProjExclusive ? { style: { background: '#fff1b8' } } : {};
            }}
            columns={[
          { title: '收费规则系统编码', dataIndex: 'system_code', width: 200, render: renderRuleCode },
          { title: '操作类型', dataIndex: 'operation_type_name', width: 160 },
          { title: '收费项目名称', dataIndex: 'display_name', width: 220, render: (v: any, r: any) => v || r.operation_type_name },
          { title: '收费单位', dataIndex: 'unit_label', width: 120, render: (v: string) => v || '-' },
          { title: (
            <span>
              状态
              <Tooltip title="“启用”表示此计费规则生效并正常计费；“屏蔽”表示此计费规则未生效（不计费）">
                <QuestionCircleOutlined style={{ marginLeft: 6, color: '#999' }} />
              </Tooltip>
            </span>
          ), dataIndex: 'is_active', width: 100, render: (v: boolean, r: any) => {
            if (r?.is_masked && v) return <Tag color="red">屏蔽</Tag>;
            if (v) return <Tag color="green">启用</Tag>;
            return <Tag>禁用</Tag>;
          } },
          { title: '收费类型', dataIndex: 'rule_type', width: 100, render: (v: string) => {
            const map: any = {
              flat: { label: '固定收费', color: 'blue' },
              tiered: { label: '梯度收费', color: 'magenta' },
            };
            const key = v === 'tiered' ? 'tiered' : 'flat';
            const cfg = map[key];
            return <Tag color={cfg.color}>{cfg.label}</Tag>;
          } },
          { title: '梯度维度', dataIndex: 'tier_basis', width: 120, render: (v: string, r: any) => {
            if (r?.rule_type !== 'tiered') return '-';
            const label = ({
              QUANTITY: '数量',
              WEIGHT_G: '重量(g)',
              WEIGHT_KG: '重量(kg)',
              VOLUME_CUFT: '体积(ft³)',
              VOLUME_CBM: '体积(m³)',
              VOLUME_CC: '体积(cm³)',
              AGE_DAY: '库龄(天)'
            } as any)[v || ''];
            return label || '-';
          } },
          { title: (
            <span>
              单价
              <Tooltip title="请点击单价查看梯度收费详情">
                <QuestionCircleOutlined style={{ marginLeft: 6, color: '#999' }} />
              </Tooltip>
            </span>
          ), width: 140, render: (_: any, r: any) => {
            if (r.is_masked) {
              return <span title="屏蔽不计费">-</span>;
            }
            if (r.rule_type === 'tiered') {
              const text = r.unit_price_display || '查看梯度';
              return (
                <span
                  style={{ color: '#1677ff', cursor: 'pointer' }}
                  onClick={() => openTierView(r)}
                  title="查看梯度设置详情"
                >
                  {text}
                </span>
              );
            }
            const v = r.unit_price;
            return v != null ? Number(v).toFixed(2) : '-';
          } },
          { title: '币种', dataIndex: 'currency', width: 80 },
          { title: '说明', dataIndex: 'description', width: 360, render: (v: any) => (
            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{v || '-'}</div>
          ) },
          { title: '举例', dataIndex: 'examples', width: 360, render: (v: any) => (
            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{v || '-'}</div>
          ) },
            ]}
          />
          </div>
        ) },
      ]} />

      {/* 梯度详情（只读） */}
      <Modal title={(
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', paddingRight: 48 }}>
            <span>梯度详情 - {tierViewRule?.operation_type_name || ''}</span>
            <span>
              维度：{
                tierViewRule?.tier_basis
                  ? ({
                      QUANTITY: '数量',
                      WEIGHT_G: '重量(g)',
                      WEIGHT_KG: '重量(kg)',
                      VOLUME_CUFT: '体积(ft³)',
                      VOLUME_CBM: '体积(m³)',
                      AGE_DAY: '库龄(天)'
                    } as any)[tierViewRule.tier_basis] || '-'
                  : '-'
              }
            </span>
          </div>
        )}
        open={tierViewOpen}
        onCancel={() => setTierViewOpen(false)}
        footer={null}
        destroyOnClose>
        <div className="mb-2">
          <Alert
            type="info"
            showIcon
            message="梯度区间说明"
            description={(
              <div>
                <div>区间最小表示 ≥（包含），区间最大表示 ＜（不包含）。</div>
              </div>
            )}
            className="mb-2"
          />
        </div>
        <Table
          rowKey={(row, idx) => String(row.id || `tier-${idx}`)}
          loading={tierViewLoading}
          dataSource={tierViewList}
          pagination={false}
          columns={[
            { title: (
              <div>
                <div>区间最小</div>
                <div style={{ fontSize: 12, color: '#999' }}>（≥，包含）</div>
              </div>
            ), dataIndex: 'min_value', render: (v: any) => (v === null || v === undefined ? '0' : v) },
            { title: (
              <div>
                <div>区间最大</div>
                <div style={{ fontSize: 12, color: '#999' }}>（＜，不包含；空=无上限）</div>
              </div>
            ), dataIndex: 'max_value', render: (v: any) => (v === null || v === undefined ? '-' : v) },
            { title: '单价', dataIndex: 'unit_price', render: (v: any) => (v != null ? Number(v).toFixed(2) : '-') },
            { title: '展示备注', dataIndex: 'tier_remark', render: (v: any) => v || '-' },
          ]}
        />
      </Modal>

      {/* 选择项目（在切换到项目级tab且未选择项目时弹出） */}
      <Modal
        title="请选择项目"
        open={projectSelectModalOpen}
        onCancel={() => setProjectSelectModalOpen(false)}
        onOk={() => setProjectSelectModalOpen(false)}
        okText="确定"
        destroyOnClose
      >
        <div className="flex items-center gap-2">
          <Select
            showSearch
            allowClear
            placeholder="搜索项目"
            style={{ width: 360 }}
            value={projectId}
            loading={projectLoading}
            onFocus={() => searchProjects('')}
            onSearch={(v) => searchProjects(v)}
            filterOption={false}
            options={projectOptions}
            onChange={(v, opt) => {
              setProjectId(v as number | undefined);
              setProjPage(1);
              // 设置头部展示文案
              const label = (opt as any)?.label || '';
              setProjectHeaderLabel(label);
            }}
          />
          <Button onClick={() => { setProjectId(undefined); setProjectHeaderLabel(''); }}>清除</Button>
        </div>
      </Modal>
    </div>
  );
};

export default BillingRules;


